/**
 * A file doc's page body (slice 5b spec §9.3): the file's card (its kind's
 * mark, name and size, who added it, Download and Replace), then a photo as
 * it is, or the text Crew read from it, or why there's none. A file is never
 * drawn in the page otherwise: Download opens it (spec §17, 6).
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { Download, Replace } from "lucide-react";
import { useState } from "react";
import { errorHint, upload } from "../../app/api";
import { refusalOf, useSaveDoc, type Doc, type DocFile } from "../docs/api";
import { DocBlocks } from "../docs/doc-blocks";
import { fileUrl } from "../messaging/attached";
import { FilePicker } from "./file-picker";
import { kindOf, refusalFor, sizeLabel } from "./kinds";

/** Files whose text is shown as written, line for line; the rest are read as Markdown. */
const AS_WRITTEN = new Set(["text/plain", "text/csv", "application/json", "text/calendar"]);

/** Why there's no text to show, by the file's read. */
const NO_TEXT: Record<Exclude<DocFile["text_status"], "ready">, string> = {
  pending: "Crew is reading this…",
  failed: "Crew couldn't read this file.",
  none: "Crew can't read this kind of file. Download it to open it.",
};

/** Replace: one file picked, uploaded, then swapped in from the version on the page. */
function ReplaceButton({ doc }: { doc: Doc }) {
  const replace = useSaveDoc(doc.id);
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const pick = async ([file]: File[]) => {
    if (!file) return;
    const refused = refusalFor(file);
    if (refused) return setProblem(refused);
    setProblem(null);
    setUploading(true);
    try {
      const kept = await upload(file);
      await replace.mutateAsync({ file_id: kept.id, version: doc.version });
    } catch (error) {
      const conflict = refusalOf(error)?.kind === "conflict";
      setProblem(conflict ? "This doc changed since you opened it. Try again." : errorHint(error));
    } finally {
      setUploading(false);
    }
  };
  const busy = uploading || replace.isPending;
  return (
    <>
      <FilePicker
        variant="outline"
        multiple={false}
        disabled={busy}
        onFiles={(files) => void pick(files)}
        data-file-replace
      >
        {busy ? <Spinner /> : <Replace />}
        Replace
      </FilePicker>
      {problem ? (
        <p role="alert" className="basis-full text-meta text-brick">
          {problem}
        </p>
      ) : null}
    </>
  );
}

function FileCard({ doc, file }: { doc: Doc; file: DocFile }) {
  const kind = kindOf(file);
  const Icon = kind.icon;
  const archived = Boolean(doc.archived_at);
  return (
    <div data-file-card className="flex flex-wrap items-center gap-3">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-muted text-copper">
        <Icon aria-hidden className="size-5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body font-medium text-foreground">{file.name}</span>
        <span className="text-meta text-muted-foreground">
          {kind.label} · {sizeLabel(file.size)}
        </span>
      </span>
      {/* On a phone the actions take their own row, so the name and size never wrap. */}
      <span className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        <a
          href={fileUrl(file.id)}
          download={file.name}
          data-file-download
          className={buttonVariants({ variant: archived ? "outline" : "default" })}
        >
          <Download />
          Download
        </a>
        {archived ? null : <ReplaceButton doc={doc} />}
      </span>
    </div>
  );
}

/** What's under the card: the photo, the text Crew read, or why there isn't any. */
function FileText({ doc, file }: { doc: Doc; file: DocFile }) {
  if (file.kind === "photo") {
    return (
      <img
        src={fileUrl(file.id, "full")}
        alt={doc.title}
        data-file-photo
        className="h-auto max-h-[70vh] w-auto max-w-full self-start rounded-lg"
      />
    );
  }
  if (file.text_status !== "ready" || !doc.body.trim()) {
    const why =
      file.text_status === "ready" ? "Crew found no text in this file." : NO_TEXT[file.text_status];
    return (
      <p data-file-text={file.text_status} className="text-body text-muted-foreground">
        {why}
      </p>
    );
  }
  return (
    <section data-file-text="ready" className="flex flex-col gap-3">
      <h2 className="text-meta font-medium tracking-wide text-muted-foreground uppercase">
        Text Crew read
      </h2>
      {AS_WRITTEN.has(file.mime.split(";")[0]!.trim()) ? (
        <pre className="max-h-[70vh] overflow-auto font-mono text-[13px] leading-5 whitespace-pre-wrap text-foreground">
          {doc.body}
        </pre>
      ) : (
        <DocBlocks body={doc.body} />
      )}
    </section>
  );
}

/** A file doc's body: on a white sheet at 1440, the page itself on a phone, as a doc's is. */
export function FileDocBody({ doc, mobile }: { doc: Doc; mobile: boolean }) {
  const file = doc.file;
  if (!file) return null;
  const archived = Boolean(doc.archived_at);
  return (
    <article
      data-doc-body
      data-file-doc
      className={cn(
        "flex min-w-0 flex-col gap-6",
        mobile
          ? "border-t border-border pt-5"
          : "rounded-panel bg-card px-12 py-10 shadow-[0_0_0_1px_rgb(31_42_68/8%),0_8px_24px_rgb(31_42_68/4%)]",
        archived && (mobile ? "opacity-60" : "opacity-70"),
      )}
    >
      <FileCard doc={doc} file={file} />
      <div className="border-t border-border pt-6">
        <FileText doc={doc} file={file} />
      </div>
    </article>
  );
}
